import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { EcosystemAPI, mapServerError } from "../../lib/ecosystemApi";
import { useAuth } from "../../lib/auth";
import { useCapabilities } from "../../lib/useCapabilities";

const TRUST_KINDS = ["partner", "vendor"];
const STRUCTURAL_KINDS = ["affiliate", "referral", "provider_of", "program_of", "event_host", "resource_by"];
const ALL_KINDS = [...TRUST_KINDS, ...STRUCTURAL_KINDS];

const STATE_BADGE = {
  DRAFT: "bg-slate-200 text-slate-800",
  REVIEW: "bg-amber-100 text-amber-800",
  APPROVED: "bg-emerald-100 text-emerald-800",
  ESTABLISHED: "bg-emerald-600 text-white",
  REJECTED: "bg-rose-100 text-rose-800",
  INACTIVE: "bg-slate-400 text-white",
  ARCHIVED: "bg-slate-500 text-white",
};

export default function Relationships() {
  const { user } = useAuth();
  const { caps } = useCapabilities();
  const [items, setItems] = useState([]);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [reasonFor, setReasonFor] = useState(null); // {rel, action}
  // BTC3 Phase 3 — capability-driven visibility (backend enforces
  // authorization; these bits only hide/show buttons).
  const canApprove = !!caps.ecosystem_relationship_approve;
  const canRevoke = !!caps.ecosystem_relationship_revoke;

  const load = async () => {
    setLoading(true);
    try {
      const [rels, ents] = await Promise.all([
        EcosystemAPI.listRelationships(),
        EcosystemAPI.list({ limit: 100 }),
      ]);
      setItems(rels?.items || []);
      setEntities(ents || []);
    } catch (e) { toast.error(mapServerError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const call = async (fn, msg) => {
    try { await fn(); toast.success(msg); await load(); }
    catch (e) { toast.error(mapServerError(e)); }
  };

  const entName = (id) => entities.find((e) => e.id === id)?.canonical_name || id;

  return (
    <div data-testid="relationships-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900" data-testid="relationships-heading">Relationships</h1>
          <p className="text-xs text-slate-500 mt-1">Governed operational connections between ecosystem entities. Trust relationships (partner, vendor) require review; structural relationships are established directly.</p>
        </div>
        <button data-testid="rel-new-btn" className="px-3 py-2 bg-slate-900 text-white rounded text-sm hover:bg-slate-800" onClick={() => setShowCreate(true)}>+ New Relationship</button>
      </div>

      {showCreate && (
        <CreateRelForm
          entities={entities}
          onCancel={() => setShowCreate(false)}
          onCreated={async (rel) => {
            setShowCreate(false);
            toast.success(`Relationship created (${rel.status}).`);
            await load();
          }}
        />
      )}

      <div className="bg-white border rounded overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]" data-testid="rel-table">
          <thead className="bg-slate-50 text-xs text-slate-600">
            <tr><th className="p-2 text-left">Kind</th><th className="p-2 text-left">From</th><th className="p-2 text-left">To</th><th className="p-2 text-left">Status</th><th className="p-2 text-left">Actions</th></tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="p-4 text-center text-slate-500">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-500" data-testid="rel-empty">No relationships yet.</td></tr>}
            {items.map((r) => (
              <tr key={r.id} className="border-t" data-testid={`rel-row-${r.id}`}>
                <td className="p-2 font-mono text-xs">{r.relationship_kind}</td>
                <td className="p-2">{entName(r.from_entity_id)}</td>
                <td className="p-2">{entName(r.to_entity_id)}</td>
                <td className="p-2"><span className={`px-2 py-0.5 rounded text-xs ${STATE_BADGE[r.status] || "bg-slate-200"}`} data-testid={`rel-status-${r.id}`}>{r.status}</span></td>
                <td className="p-2 space-x-1">
                  {r.status === "DRAFT" && r.created_by === user?.id && (
                    <button data-testid={`rel-submit-${r.id}`} className="px-2 py-1 text-xs bg-slate-100 hover:bg-slate-200 rounded" onClick={() => call(() => EcosystemAPI.submitRelationship(r.id), "Submitted for review.")}>Submit</button>
                  )}
                  {r.status === "REVIEW" && canApprove && r.created_by !== user?.id && (
                    <>
                      <button data-testid={`rel-approve-${r.id}`} className="px-2 py-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded" onClick={() => call(() => EcosystemAPI.approveRelationship(r.id), "Approved & established.")}>Approve</button>
                      <button data-testid={`rel-reject-${r.id}`} className="px-2 py-1 text-xs bg-rose-100 hover:bg-rose-200 rounded" onClick={() => setReasonFor({ rel: r, action: "reject" })}>Reject</button>
                    </>
                  )}
                  {r.status === "ESTABLISHED" && canApprove && (
                    <button data-testid={`rel-deactivate-${r.id}`} className="px-2 py-1 text-xs bg-slate-100 hover:bg-slate-200 rounded" onClick={() => setReasonFor({ rel: r, action: "deactivate" })}>Deactivate</button>
                  )}
                  {r.status !== "INACTIVE" && r.status !== "ARCHIVED" && canRevoke && (
                    <button data-testid={`rel-emergency-${r.id}`} className="px-2 py-1 text-xs bg-red-700 hover:bg-red-800 text-white rounded" onClick={() => setReasonFor({ rel: r, action: "emergency" })}>Emergency Revoke</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {reasonFor && (
        <ReasonDialog
          title={reasonFor.action === "reject" ? "Reject relationship" : reasonFor.action === "deactivate" ? "Deactivate relationship" : "Emergency revoke"}
          onCancel={() => setReasonFor(null)}
          onSubmit={async (reason) => {
            const fn =
              reasonFor.action === "reject" ? () => EcosystemAPI.rejectRelationship(reasonFor.rel.id, reason)
              : reasonFor.action === "deactivate" ? () => EcosystemAPI.deactivateRelationship(reasonFor.rel.id, reason)
              : () => EcosystemAPI.emergencyRevokeRelationship(reasonFor.rel.id, reason);
            setReasonFor(null);
            await call(fn, `Relationship ${reasonFor.action}ed.`);
          }}
        />
      )}
    </div>
  );
}

function CreateRelForm({ entities, onCancel, onCreated }) {
  const [kind, setKind] = useState("partner");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [evidenceUrl, setEvidenceUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const isTrust = TRUST_KINDS.includes(kind);
  const submit = async () => {
    if (!from || !to || from === to) { toast.error("Select two distinct entities."); return; }
    setBusy(true);
    try {
      const rel = await EcosystemAPI.createRelationship({
        relationship_kind: kind, from_entity_id: from, to_entity_id: to,
        evidence_url: evidenceUrl || null, evidence_notes: notes || null,
      });
      onCreated(rel);
    } catch (e) { toast.error(mapServerError(e)); }
    setBusy(false);
  };
  return (
    <div className="bg-white border rounded p-4 space-y-3" data-testid="rel-create-form">
      <div className="text-sm font-semibold text-slate-900">New relationship</div>
      <div className="text-xs text-slate-500">{isTrust ? "Trust relationship: will require review before establishing." : "Structural relationship: establishes immediately (server-authorised)."}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block text-xs text-slate-700">Kind
          <select data-testid="rel-kind" value={kind} onChange={(e) => setKind(e.target.value)} className="mt-1 w-full border rounded p-2 text-sm">
            <optgroup label="Trust">{TRUST_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}</optgroup>
            <optgroup label="Structural">{STRUCTURAL_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}</optgroup>
          </select>
        </label>
        <label className="block text-xs text-slate-700">Evidence URL
          <input data-testid="rel-evidence-url" value={evidenceUrl} onChange={(e) => setEvidenceUrl(e.target.value)} placeholder="https://…" className="mt-1 w-full border rounded p-2 text-sm" />
        </label>
        <label className="block text-xs text-slate-700">From entity
          <select data-testid="rel-from" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 w-full border rounded p-2 text-sm">
            <option value="">— select —</option>
            {entities.map((e) => <option key={e.id} value={e.id}>{e.canonical_name} · {e.entity_type}</option>)}
          </select>
        </label>
        <label className="block text-xs text-slate-700">To entity
          <select data-testid="rel-to" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 w-full border rounded p-2 text-sm">
            <option value="">— select —</option>
            {entities.map((e) => <option key={e.id} value={e.id}>{e.canonical_name} · {e.entity_type}</option>)}
          </select>
        </label>
      </div>
      <label className="block text-xs text-slate-700">Evidence notes
        <textarea data-testid="rel-evidence-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1 w-full border rounded p-2 text-sm" />
      </label>
      <div className="flex gap-2 pt-1">
        <button data-testid="rel-create-submit" disabled={busy} className="px-3 py-2 bg-slate-900 text-white text-sm rounded hover:bg-slate-800 disabled:opacity-50" onClick={submit}>{busy ? "Creating…" : "Create"}</button>
        <button data-testid="rel-create-cancel" className="px-3 py-2 bg-slate-100 text-sm rounded hover:bg-slate-200" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function ReasonDialog({ title, onCancel, onSubmit }) {
  const [reason, setReason] = useState("");
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="rel-reason-dialog">
      <div className="bg-white rounded shadow-lg max-w-md w-full p-4 space-y-3">
        <div className="text-sm font-semibold">{title}</div>
        <textarea data-testid="rel-reason-input" autoFocus value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Reason (required)" className="w-full border rounded p-2 text-sm" />
        <div className="flex gap-2 justify-end">
          <button data-testid="rel-reason-cancel" className="px-3 py-2 bg-slate-100 text-sm rounded" onClick={onCancel}>Cancel</button>
          <button data-testid="rel-reason-confirm" disabled={!reason.trim()} className="px-3 py-2 bg-red-700 text-white text-sm rounded disabled:opacity-50" onClick={() => onSubmit(reason.trim())}>Confirm</button>
        </div>
      </div>
    </div>
  );
}
